<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils';

const avatarVariants = cva(
  'relative flex shrink-0 overflow-hidden rounded-full bg-slate-800',
  {
    variants: {
      size: {
        sm: 'h-8 w-8 text-xs',
        md: 'h-10 w-10 text-sm',
        lg: 'h-14 w-14 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

interface Props {
  src?: string;
  alt?: string;
  fallback?: string;
  size?: VariantProps<typeof avatarVariants>['size'];
  class?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  alt: 'Avatar',
});

const initials = computed(() => {
  if (props.fallback) return props.fallback;
  return props.alt.slice(0, 2).toUpperCase();
});
</script>

<template>
  <div :class="cn(avatarVariants({ size }), props.class)">
    <img
      v-if="src"
      :src="src"
      :alt="alt"
      class="aspect-square h-full w-full object-cover"
    />
    <div
      v-else
      class="flex h-full w-full items-center justify-center rounded-full bg-slate-800 text-slate-400 font-medium"
    >
      {{ initials }}
    </div>
  </div>
</template>
